import { computed } from "vue";
import { defineStore } from "pinia";
import {
  type Claim,
  RootClaims,
  claimsLabels,
  ClaimsTreeMap,
  additionalAccessClaims,
} from "@/common/claims/claimsEnum";

export interface ClaimItem {
  label: string;
  value: Claim;
}

export interface Option {
  label: string;
  value: Claim;
  children?: Option[];
}

export const useClaims = defineStore("claims", () => {
  const parseClaims = (claimsStr: string): Set<Claim> => {
    return new Set(
      claimsStr?.split(",").map((claim) => +claim.trim() as Claim) ?? [],
    );
  };

  const claimsList = computed<ClaimItem[]>((): ClaimItem[] =>
    Object.entries(claimsLabels).map(([key, label]) => ({
      label,
      value: +key,
    })),
  );

  const options = computed<Option[]>((): Option[] => {
    const res: Option[] = [];

    for (const rootClaim of RootClaims) {
      const option: Option = {
        label: claimsLabels[rootClaim as keyof typeof claimsLabels],
        value: rootClaim,
        children: [],
      };

      option.children = (
        ClaimsTreeMap[rootClaim as keyof typeof ClaimsTreeMap] ?? []
      ).map((child: Claim) => {
        return {
          label: claimsLabels[child as keyof typeof claimsLabels],
          value: child,
        };
      });

      res.push(option);
    }

    return res;
  });

  const additionalOptions = computed<Option[]>((): Option[] =>
    additionalAccessClaims.map((el: Claim) => ({
      label: claimsLabels[el as keyof typeof claimsLabels],
      value: el,
      children: [],
    })),
  );

  return {
    claimsList,
    parseClaims,
    options,
    additionalOptions,
  };
});
